<template>
  <div class="d3-chart-wrapper" style="position: relative;">
    <chart-toolbar
      :locked="!!lockedY"
      :zoomed="!!zoomRange"
      :lockable="!!tkCurrentDomains"
      @lock="tkToggleLockY(tkCurrentDomains)"
      @reset="tkResetZoom()"
      @png="tkExportPng('fuel-characteristics')"
      @csv="tkExportCsvNow()"
    />
    <div ref="chartContainer" class="chart-container"></div>
    <div ref="tooltip" class="d3-tooltip" style="opacity: 0; pointer-events: none; position: absolute; background: rgba(0, 0, 0, 0.8); color: #fff; padding: 8px; border-radius: 4px; font-size: 12px; z-index: 10;"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatDateTime } from '@/util/date';
import { CHART_AXIS, CHART_TOOLTIP } from '@/util/apiDatetime';
import { interpolateNulls } from '@/util/summary/series';
import chartToolkit from './chartToolkit';
import ChartToolbar from './ChartToolbar.vue';

export default {
  name: 'FuelCharacteristics',
  mixins: [chartToolkit],
  components: { ChartToolbar },
  props: ['series', 'type', 'deviceStatus'],
  data() {
    return {
      resizeObserver: null,
      tkCurrentDomains: null,
    };
  },
  created() {
    this.tkLast = null;
  },
  mounted() {
    this.tkBind();
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    this.tkUnbind();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    series: { deep: true, handler: 'drawChart' },
  },
  methods: {
    // Reset mengembalikan KETIGA chart, bukan cuma yang ini.
    tkResetZoom() {
      this.tkPublishZoom(null);
    },
    tkExportCsvNow() {
      if (!this.tkLast) return;
      this.tkExportCsv('fuel-characteristics', this.tkLast.timestamps, this.tkLast.datasets);
    },

    processData() {
      if (!this.series || !this.type) return null;

      const upperType = this.type.toUpperCase();
      const titleCaseType = this.type.charAt(0).toUpperCase() + this.type.slice(1).toLowerCase();

      const mapData = (path) => this.series.map((data) => {
        const keys = path.split('.');
        let val = data;
        // eslint-disable-next-line no-restricted-syntax
        for (const key of keys) val = val && val[key] ? val[key] : null;
        const num = Number(val);
        return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
      });

      const inFlow = mapData(`fmData.${upperType}_IN.volumeFlowrate`);
      const Rpm = mapData(`rpmData.${upperType}.rpm`);
      const RpmMax = mapData(`rpmData.${upperType}.rpmMax`);
      const Dens = mapData(`fmData.${upperType}_IN.density`);
      const Temp = mapData(`fmData.${upperType}_IN.temperature`);

      const timestamps = this.series.map((d) => new Date(d.timestamp * 1000));

      const datasets = [];

      datasets.push({
        name: `${titleCaseType} In Flow (L/h)`, data: inFlow, interpolated: interpolateNulls(inFlow), color: '#0084ff', axis: 'flow',
      });

      // In-out ditentukan per-group (label-driven). Group dengan hasOut -> tampilkan
      // out/net; fallback ke flag global flowmeterType kalau group tak ketemu.
      const group = (this.deviceStatus?.flowmeterGroups || []).find(
        (g) => String(g.name).toUpperCase() === upperType,
      );
      const groupHasOut = group
        ? group.hasOut
        : (this.deviceStatus && this.deviceStatus.flowmeterType === 'in-out');

      if (groupHasOut) {
        const outFlow = mapData(`fmData.${upperType}_OUT.volumeFlowrate`);
        datasets.push({
          name: `${titleCaseType} Out Flow (L/h)`, data: outFlow, interpolated: interpolateNulls(outFlow), color: '#ff0000', axis: 'flow',
        });

        const netFlow = inFlow.map((val, i) => {
          if (val !== null && outFlow[i] !== null) {
            return Number((val - outFlow[i]).toFixed(2));
          }
          return null;
        });
        datasets.push({
          name: `${titleCaseType} Net Flow (L/h)`, data: netFlow, interpolated: interpolateNulls(netFlow), color: '#00ff00', axis: 'flow',
        });
      }

      datasets.push({
        name: `${titleCaseType} RPM`,
        data: Rpm,
        interpolated: interpolateNulls(Rpm),
        color: '#884DFF',
        axis: 'rpm',
        peak: RpmMax.some((v) => v !== null) ? RpmMax : null,
      });
      datasets.push({
        name: `${titleCaseType} Density (g/cm³)`, data: Dens, interpolated: interpolateNulls(Dens), color: '#10B981', axis: 'dens',
      });
      datasets.push({
        name: `${titleCaseType} Temp (°C)`, data: Temp, interpolated: interpolateNulls(Temp), color: '#F97316', axis: 'temp',
      });

      return { timestamps, datasets };
    },

    drawChart() {
      const processed = this.processData();
      if (!processed) return;
      const { timestamps, datasets } = processed;
      const visible = this.tkVisible(datasets);
      const container = this.$refs.chartContainer;
      d3.select(container).selectAll('*').remove();

      const visibleWidth = this.$el.clientWidth || container.clientWidth;
      const isCompact = visibleWidth < 576;
      const chartOuterWidth = Math.max(container.clientWidth, isCompact ? 900 : 0);

      // Margin kanan desktop diseragamkan biar crosshair antar chart sejajar.
      const margin = {
        top: isCompact ? 56 : 40,
        right: isCompact ? 170 : 180,
        bottom: isCompact ? 48 : 50,
        left: isCompact ? 52 : 60,
      };
      const width = Math.max(1, chartOuterWidth - margin.left - margin.right);
      const chartHeight = isCompact ? 440 : 600;
      const height = chartHeight - margin.top - margin.bottom;
      const xTickCount = isCompact ? 5 : 8;
      const yTickCount = isCompact ? 5 : 10;
      const densAxisOffset = isCompact ? 56 : 60;

      const svg = d3.select(container).append('svg')
        .attr('width', chartOuterWidth)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Tanpa ini garis meluber ke luar kotak pas sumbu dikunci.
      const clipId = `tk-klip-${this._uid}`;
      svg.append('defs').append('clipPath').attr('id', clipId)
        .append('rect')
        .attr('width', width)
        .attr('height', height);
      const dataLayer = svg.append('g').attr('clip-path', `url(#${clipId})`);

      const x = d3.scaleTime().domain(this.tkXDomain(timestamps)).range([0, width]);
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(xTickCount).tickFormat((d) => formatDateTime(d, CHART_AXIS, null, false)))
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');

      // Scales — semua domain dihitung dulu, baru garis nolnya disejajarkan.
      const rpmPeakAll = visible.filter((d) => d.axis === 'rpm')
        .reduce((acc, d) => (d.peak ? acc.concat(d.peak) : acc), []);
      const [flowDomain, rpmDomain, densDomain, tempDomain] = this.tkAlignZero([
        this.tkDomain('flow', visible, 10),
        this.tkDomain('rpm', visible, 1000, rpmPeakAll),
        this.tkDomain('dens', visible, 1),
        this.tkDomain('temp', visible, 100),
      ]);
      const yFlow = d3.scaleLinear().domain([flowDomain[0] * 1.1, flowDomain[1] * 1.1]).range([height, 0]);

      svg.append('g').call(d3.axisLeft(yFlow).ticks(yTickCount))
        .append('text').attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', isCompact ? -36 : -40)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text('Flow (L/h)');

      // Garis nol biar batas konsumsi sama aliran balik (net flow) kebaca.
      if (flowDomain[0] < 0) {
        dataLayer.append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yFlow(0))
          .attr('y2', yFlow(0))
          .attr('stroke', '#9CA3AF')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '2 2');
      }

      // Sumbu kanan rpm/dens/temp — hanya digambar kalau serinya tampil, dan menguncup kalau ada yang disembunyikan.
      const hasRpmAxis = visible.some((d) => d.axis === 'rpm');
      const hasDensAxis = visible.some((d) => d.axis === 'dens');
      const hasTempAxis = visible.some((d) => d.axis === 'temp');

      const [, maxRpm] = rpmDomain;
      const yRpm = d3.scaleLinear()
        .domain([rpmDomain[0], (Math.ceil(maxRpm / 100) * 100) + 100]).range([height, 0]);

      const [, maxDens] = densDomain;
      const yDens = d3.scaleLinear().domain([densDomain[0], maxDens * 1.1]).range([height, 0]);

      const [, maxTemp] = tempDomain;
      const yTemp = d3.scaleLinear().domain([tempDomain[0], maxTemp * 1.1]).range([height, 0]);

      const axisStep = densAxisOffset;
      let axisOffset = 0;

      if (hasRpmAxis) {
        const rpmDataset = datasets.find((d) => d.axis === 'rpm');
        svg.append('g').attr('transform', `translate(${width + axisOffset}, 0)`).call(d3.axisRight(yRpm).ticks(yTickCount))
          .append('text')
          .attr('fill', rpmDataset.color)
          .attr('transform', 'rotate(-90)')
          .attr('y', 30)
          .attr('dy', '0.71em')
          .style('text-anchor', 'end')
          .text(rpmDataset.name);
        axisOffset += axisStep;
      }

      if (hasDensAxis) {
        const densDataset = datasets.find((d) => d.axis === 'dens');
        svg.append('g').attr('transform', `translate(${width + axisOffset}, 0)`).call(d3.axisRight(yDens).ticks(yTickCount))
          .append('text')
          .attr('fill', densDataset.color)
          .attr('transform', 'rotate(-90)')
          .attr('y', 30)
          .attr('dy', '0.71em')
          .style('text-anchor', 'end')
          .text(densDataset.name);
        axisOffset += axisStep;
      }

      if (hasTempAxis) {
        const tempDataset = datasets.find((d) => d.axis === 'temp');
        svg.append('g').attr('transform', `translate(${width + axisOffset}, 0)`).call(d3.axisRight(yTemp).ticks(yTickCount))
          .append('text')
          .attr('fill', tempDataset.color)
          .attr('transform', 'rotate(-90)')
          .attr('y', 30)
          .attr('dy', '0.71em')
          .style('text-anchor', 'end')
          .text(tempDataset.name);
      }

      svg.append('g').attr('class', 'grid').attr('opacity', 0.1).call(d3.axisLeft(yFlow).ticks(yTickCount).tickSize(-width).tickFormat(''));

      const getScale = (axisName) => {
        if (axisName === 'flow') return yFlow;
        if (axisName === 'rpm') return yRpm;
        if (axisName === 'dens') return yDens;
        return yTemp;
      };

      // Simpan tuple mentah dari tkDomain, bukan .domain() yang udah dihias — biar kunci ga ikut menghias ulang.
      this.tkCurrentDomains = {
        flow: flowDomain, rpm: rpmDomain, dens: densDomain, temp: tempDomain,
      };
      // Ekspor ngikut yang keliatan: kena zoom dan seri yang disembunyiin.
      const [xAwal, xAkhir] = x.domain();
      const visibleIndices = [];
      timestamps.forEach((t, i) => {
        if (t >= xAwal && t <= xAkhir) visibleIndices.push(i);
      });
      this.tkLast = {
        timestamps: visibleIndices.map((i) => timestamps[i]),
        datasets: visible.map((d) => ({ name: d.name, data: visibleIndices.map((i) => d.data[i]) })),
      };

      // Pita puncak: jarak dari rata-rata ke nilai tertinggi di tiap bucket.
      visible.forEach((series) => {
        if (!series.peak) return;
        const peakScale = getScale(series.axis);
        const points = series.interpolated
          .map((value, i) => ({ i, base: value, peak: series.peak[i] }));
        // Diputus lewat defined biar area ga nyambung lintas jeda.
        const hasPeak = (d) => d.base !== null && d.peak !== null && d.peak > d.base;
        dataLayer.append('path')
          .datum(points)
          .attr('fill', series.color)
          .attr('opacity', 0.18)
          .style('pointer-events', 'none')
          .attr('d', d3.area()
            .defined(hasPeak)
            .x((d) => x(timestamps[d.i]))
            .y0((d) => peakScale(d.base))
            .y1((d) => peakScale(d.peak)));
      });

      // Draw Lines & Dots
      const realLineWidth = 2.5;
      const hoverLineWidth = 4;
      const interpolatedLineWidth = 1.5;
      visible.forEach((series, index) => {
        const yScale = getScale(series.axis);
        // Line Interpolated
        dataLayer.append('path')
          .datum(series.interpolated)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', interpolatedLineWidth)
          .attr('stroke-dasharray', '4 4')
          .attr('opacity', 0.5)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)));

        // Line Real
        dataLayer.append('path')
          .datum(series.data)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', realLineWidth)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)))
          .on('mouseover', function handleMouseOver() {
            d3.select(this).attr('stroke-width', hoverLineWidth);
          })
          .on('mouseout', function handleMouseOut() {
            d3.select(this).attr('stroke-width', realLineWidth);
          });

        // Dots
        dataLayer.selectAll(`.dot-series-${index}`)
          .data(series.data.map((d, i) => ({ value: d, date: timestamps[i] })).filter((d) => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', `dot-series-${index}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');
      });

      // Tooltip
      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;

      const focusGroup = svg.append('g').style('display', 'none');
      const focusLine = focusGroup.append('line').attr('stroke', '#aaa').attr('stroke-dasharray', '3 3').attr('y1', 0)
        .attr('y2', height);
      const focusCircles = visible.map((ds) => focusGroup.append('circle').attr('r', 6).attr('fill', ds.color).attr('stroke', '#fff')
        .attr('stroke-width', 2));

      this.tkOnHover((ms) => {
        const [first, last] = x.domain();
        if (ms === null || ms < +first || ms > +last) {
          focusGroup.style('display', 'none');
          return;
        }
        focusGroup.style('display', null);
        focusLine.attr('x1', x(new Date(ms))).attr('x2', x(new Date(ms)));
      });

      const legendSpacing = 18;
      const legendItemWidth = 160;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendWidth = Math.min(datasets.length, legendColumns) * legendItemWidth;
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - legendWidth) / 2)}, -25)`);
      const legendItem = legendGroup.selectAll('g')
        .data(datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * legendSpacing})`;
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          this.tkToggleSeries(d.name);
        });

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '12px')
        .text((d) => d.name);

      legendItem.select('rect').attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.3 : 1));
      legendItem.select('text')
        .attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.4 : 1))
        .style('text-decoration', (d) => (this.hiddenSeries[d.name] ? 'line-through' : 'none'));

      const hoverBase = this.tkInstallBrush(svg, x, width, height);
      hoverBase
        .on('mouseover', () => { tooltip.style('opacity', 1); focusGroup.style('display', null); })
        .on('mouseout', () => { tooltip.style('opacity', 0); focusGroup.style('display', 'none'); this.tkPublishHover(null); })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          // eslint-disable-next-line no-nested-ternary
          const index = i < timestamps.length && i > 0 ? (x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1) : 0;
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);
          this.tkPublishHover(+date);

          visible.forEach((ds, idx) => {
            const val = ds.data[index];
            const yScale = getScale(ds.axis);
            if (val !== null && val !== undefined) {
              focusCircles[idx].attr('cx', xPos).attr('cy', yScale(val)).style('display', null);
            } else {
              focusCircles[idx].style('display', 'none');
            }
          });

          let html = `<strong>${formatDateTime(date, CHART_TOOLTIP)}</strong><br/>`;
          visible.forEach((ds) => {
            const val = ds.data[index];
            if (val !== null && val !== undefined) html += `<div style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:${ds.color};"></span>${ds.name}: ${val}</div>`;
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          const top = posY + 15;
          if (left + 160 > chartOuterWidth) left = posX - 170;
          tooltip.html(html).style('left', `${left}px`).style('top', `${top}px`);
        });
    },
  },
};
</script>

<style scoped>
.d3-chart-wrapper { font-family: inherit; }
/* d3-axis nempelin font-family="sans-serif" di grup axis; CSS menang atas atribut itu. */
.d3-chart-wrapper ::v-deep [font-family] { font-family: inherit; }

@media screen and (max-width: 575px) {
  .d3-chart-wrapper {
    min-width: 900px;
  }
}
</style>
